import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  items: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 1,
  },
  lastParams: null,
  query: { search: "", limit: 10, page: 1 },
  status: "idle",
  error: null,
  success: null,
  loading: false,
};

export const getTypes = createAsyncThunk(
  "type/getTypes",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/type`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getTypesPaginated = createAsyncThunk(
  "type/getTypesPaginated",
  async ({ restaurantId, search, limit, page }) => {
    try {
      const response = await axios.get(`${apiUrl}/type`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          limit,
          page,
        },
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addType = createAsyncThunk(
  "type/addType",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/type`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyType = createAsyncThunk(
  "type/modifyType",
  async ({ body, typeId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/type/update/${typeId}`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);
export const deleteType = createAsyncThunk(
  "type/deleteType",
  async ({ typeId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/type/${typeId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const typeSlice = createSlice({
  name: "type",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
    setTypesQuery: (state, action) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetTypesQuery: (state) => {
      state.query = { search: "", limit: 10, page: 1 };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getTypes.pending, (state, action) => {
        state.status = "loadingFetchData";
        state.loading = true;
      })
      .addCase(getTypes.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getTypes.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getTypesPaginated.pending, (state, action) => {
        state.status = "loadingFetchDataPaginated";
        state.loading = true;
      })
      .addCase(getTypesPaginated.fulfilled, (state, action) => {
        state.status = "fetchDataPaginated";
        state.loading = false;
        state.items = action.payload.types || [];
        state.pagination = action.payload.pagination;
        state.lastParams = JSON.stringify(action.meta.arg);
      })
      .addCase(getTypesPaginated.rejected, (state, action) => {
        state.status = "fetchErrorPaginated";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addType.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addType.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
        // Note: the backend does not return the created type's data, only a
        // message, so it cannot be patched into state.items here — screens
        // relying on this rely on remounting/refetching to see the new item.
      })
      .addCase(addType.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteType.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteType.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
        const deletedId = action.meta?.arg?.typeId;
        if (deletedId) {
          state.items = state.items.filter((item) => item._id !== deletedId);
          if (state.pagination) {
            state.pagination.totalRecords = Math.max(
              0,
              (state.pagination.totalRecords || 0) - 1
            );
          }
        }
      })
      .addCase(deleteType.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyType.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyType.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
        const updatedType = action.payload?.type;
        if (updatedType && updatedType._id) {
          const typeIndex = state.items.findIndex(
            (item) => item._id === updatedType._id
          );
          if (typeIndex !== -1) {
            state.items[typeIndex] = updatedType;
          }
        }
      })
      .addCase(modifyType.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});
export const { updateStatus, setTypesQuery, resetTypesQuery } =
  typeSlice.actions;
export const selectAllTypes = (state) => state.type.items;
export const selectTypesPagination = (state) => state.type.pagination;
export const selectTypesLastParams = (state) => state.type.lastParams;
export const selectTypesQuery = (state) => state.type.query;
export const getTypesStatus = (state) => state.type.status;
export const getTypesError = (state) => state.type.error;
export const getTypesSuccess = (state) => state.type.success;
export const getTypesLoading = (state) => state.type.loading;
export default typeSlice.reducer;
